import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FacilityEvent } from "@app/shared/src/models";
import {
    AlertsIcon,
    Card,
    CardHead,
    ChartLegend,
    GaugeArc,
    GaugeIcon,
    HBarList,
    LineAreaChart,
    MPS_TO_MPH,
    Note,
    Spinner,
    StackedBarChart,
    Tag,
    TrendIcon,
    TruckPanelIcon,
    formatAgo,
} from "@app/shared/src/components_v2";
import { deviceAttention } from "../../helpers/deviceAttention";
import { CATEGORY_COLOR, CATEGORY_LABEL, EventCategory, categorise, eventSpeedMph } from "../../helpers/eventDisplay";
import { useOrgEvents } from "../../hooks/useOrgEvents";
import { usePreDeploymentTests } from "../../hooks/usePreDeploymentTests";
import { summariseVolume, useTraffic } from "../../hooks/useTraffic";
import { useZoneNames } from "../../hooks/useZoneNames";
import { useFleet } from "../providers/FleetProvider";
import { useOrg } from "../providers/OrgProvider";

/**
 * The director's first screen: is the fleet protecting people right now, and what happened lately.
 *
 * Every number is computed from real endpoints — device heartbeats, the raw event stream bucketed
 * locally, and the traffic report the main dashboard charts with. Where a window was truncated
 * (very busy sites), the affected cards say so instead of presenting a partial count as the total.
 *
 * The fleet these numbers describe is `statsDevices`, not the whole organization: attenuator units
 * and whatever shares a group with them. An organization can run other Omnisight products, and a
 * gantry-mounted counter sitting in every gauge would read as a truck that never checks in and
 * never tests its relays. See FleetProvider for how that scope is drawn.
 */

const STALE_MS = 15 * 60 * 1000;
const WEEK_MS = 14 * 24 * 60 * 60 * 1000;
const DAYS = 14;

const CHART_CATEGORIES: EventCategory[] = ["collision", "speeding", "left", "right"];

/** The Devices page showing exactly the set this card lists. */
const ATTENTION_LIST = "/devices?filter=attention";

export const OverviewPage: React.FC = () => {
    const { org } = useOrg();
    const { statsDevices: devices, isLoading: fleetLoading, error: fleetError, deviceSite } = useFleet();
    const zoneNames = useZoneNames();
    const deviceIds = useMemo(() => devices.map((d) => d.id!).filter(Boolean), [devices]);
    const orgEvents = useOrgEvents(org?.id, DAYS, deviceIds);
    const traffic = useTraffic(deviceIds, DAYS);
    const preDeployment = usePreDeploymentTests(devices);

    const now = Date.now();
    const heartbeatAge = (d: (typeof devices)[number]) => now - new Date(d.lastHeartbeat ?? 0).getTime();
    const online = devices.filter((d) => heartbeatAge(d) <= STALE_MS);
    const staleTwoWeeks = devices.filter((d) => heartbeatAge(d) > WEEK_MS);

    // Day buckets, oldest → newest, keyed by local date.
    const dayKeys = useMemo(() => {
        const keys: string[] = [];
        for (let i = DAYS - 1; i >= 0; i--) keys.push(new Date(now - i * 86_400_000).toDateString());
        return keys;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Math.floor(now / 86_400_000)]);
    const dayLabels = dayKeys.map((k) => new Date(k).toLocaleDateString(undefined, { month: "short", day: "numeric" }));

    const categoriseEvent = (e: FacilityEvent) => categorise(e, e.detectionZoneId ? zoneNames.get(e.detectionZoneId) : undefined);

    const buckets = useMemo(() => {
        const perDay = new Map(dayKeys.map((k) => [k, { collision: 0, speeding: 0, left: 0, right: 0, other: 0, total: 0 }]));
        const perType: Record<EventCategory, number> = { collision: 0, speeding: 0, left: 0, right: 0, other: 0 };
        const speeds: number[] = [];
        let last24h = 0;
        let collisions24h = 0;

        for (const e of orgEvents.events) {
            const when = new Date(e.dateCreated ?? 0).getTime();
            const cat = categoriseEvent(e);
            const bucket = perDay.get(new Date(when).toDateString());
            if (bucket) {
                bucket[cat] += 1;
                bucket.total += 1;
            }
            perType[cat] += 1;
            // Through the same helper the tables use, so the distribution can't be polluted by
            // impending-collision events, whose eventValue is milliseconds rather than m/s.
            const mph = eventSpeedMph(e);
            if (mph !== undefined && mph < 200) speeds.push(mph);
            if (now - when <= 86_400_000) {
                last24h += 1;
                if (cat === "collision") collisions24h += 1;
            }
        }
        return { perDay, perType, speeds, last24h, collisions24h };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgEvents.events, zoneNames, dayKeys]);

    // Traffic per day aligned to the same buckets.
    const trafficByDay = useMemo(() => {
        const m = new Map<string, { count: number; avg: number }>();
        for (const r of traffic.data ?? []) {
            if (!r.periodStart) continue;
            m.set(new Date(r.periodStart).toDateString(), { count: r.count ?? 0, avg: r.avgVelocity ?? 0 });
        }
        return m;
    }, [traffic.data]);
    const volume24h = useMemo(() => {
        const today = trafficByDay.get(new Date(now).toDateString())?.count ?? 0;
        return today;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trafficByDay]);
    const volumeSummary = summariseVolume(traffic.data);

    const speedBands = useMemo(() => {
        const bands = [
            { label: "Under 55", min: 0, max: 55 },
            { label: "55–65", min: 55, max: 65 },
            { label: "65–75", min: 65, max: 75 },
            { label: "75–85", min: 75, max: 85 },
            { label: "Over 85", min: 85, max: Infinity },
        ];
        const total = buckets.speeds.length;
        return bands.map((b) => ({
            label: b.label,
            value: total === 0 ? 0 : Math.round((buckets.speeds.filter((s) => s >= b.min && s < b.max).length / total) * 100),
            color: "var(--accent)",
        }));
    }, [buckets.speeds]);

    /*
     * Devices only: this card answers "which trucks do I need to do something about?", and every
     * row is one you can click through to.
     *
     * The rule lives in helpers/deviceAttention so the Devices page filters on exactly the same
     * one — a card that sends you to a list that disagrees with it is worse than no card. What it
     * pointedly does not include is "not reporting right now": a TMA is powered only while it is
     * deployed, so the parked yard is normal, and "Units online" above already counts it.
     */
    const attention = useMemo(() => deviceAttention(devices), [devices]);

    if (fleetError) return <Note tone="crit">{fleetError}</Note>;

    const kpiLoading = fleetLoading || orgEvents.isLoading;

    return (
        <div className="flex flex-col gap-4">
            {/* KPI row */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    title="Units online"
                    to="/devices?filter=online"
                    icon={<TruckPanelIcon size={22} className="text-[var(--safe)]" />}
                    value={
                        fleetLoading ? "…" : (
                            <>
                                {online.length}
                                <span className="text-[17px] font-semibold text-[var(--faint)]"> / {devices.length}</span>
                            </>
                        )
                    }
                    sub={devices.length - online.length > 0 ? `${devices.length - online.length} not reporting` : "every unit is checking in"}
                    subTone={devices.length - online.length > 0 ? "warn" : "safe"}
                />
                <KpiCard
                    title="Events last 24h"
                    to="/events"
                    icon={<AlertsIcon size={22} className="text-[var(--crit)]" />}
                    value={kpiLoading ? "…" : `${buckets.last24h}${orgEvents.truncated ? "+" : ""}`}
                    sub={
                        buckets.collisions24h > 0
                            ? `${buckets.collisions24h} impending collision${buckets.collisions24h === 1 ? "" : "s"}`
                            : "no impending collisions"
                    }
                    subTone={buckets.collisions24h > 0 ? "crit" : "safe"}
                />
                <KpiCard
                    title="Traffic today"
                    icon={<TrendIcon size={22} className="text-[var(--accent)]" />}
                    value={traffic.isLoading ? "…" : volume24h.toLocaleString()}
                    sub="vehicles past all units"
                />
                <KpiCard
                    title="Avg speed"
                    icon={<GaugeIcon size={22} className="text-[var(--accent)]" />}
                    value={
                        traffic.isLoading ? "…" : volumeSummary.avgMps !== undefined ? (
                            <>
                                {Math.round(volumeSummary.avgMps * MPS_TO_MPH)}
                                <span className="text-[17px] font-semibold text-[var(--faint)]"> mph</span>
                            </>
                        ) : (
                            "—"
                        )
                    }
                    sub={`past ${DAYS} days, all zones`}
                />
            </div>

            <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
                <div className="flex min-w-0 flex-col gap-4">
                    <Card>
                        <CardHead title="Traffic & events" note={`Last ${DAYS} days`} />
                        {traffic.isLoading || orgEvents.isLoading ? (
                            <ChartLoading />
                        ) : (
                            <>
                                <LineAreaChart
                                    labels={dayLabels}
                                    series={[
                                        {
                                            key: "veh",
                                            label: "Vehicles past all units",
                                            color: "var(--accent)",
                                            area: true,
                                            values: dayKeys.map((k) => trafficByDay.get(k)?.count ?? 0),
                                        },
                                        {
                                            key: "ev",
                                            label: "Events",
                                            color: "var(--crit)",
                                            values: dayKeys.map((k) => buckets.perDay.get(k)?.total ?? 0),
                                            // Its own scale: a handful of alerts against a
                                            // quarter-million vehicles is a flat line on a shared one.
                                            axis: "right",
                                        },
                                    ]}
                                />
                                <ChartLegend
                                    items={[
                                        { label: "Vehicles past all units (left)", color: "var(--accent)" },
                                        { label: "Events (right)", color: "var(--crit)" },
                                    ]}
                                />
                            </>
                        )}
                    </Card>

                    <Card>
                        <CardHead
                            title="Events by day"
                            note={`Last ${DAYS} days · ${orgEvents.events.length}${orgEvents.truncated ? "+" : ""} total`}
                        />
                        {orgEvents.isLoading ? (
                            <ChartLoading />
                        ) : (
                            <>
                                <StackedBarChart
                                    labels={dayLabels}
                                    series={CHART_CATEGORIES.map((c) => ({
                                        key: c,
                                        label: CATEGORY_LABEL[c],
                                        color: CATEGORY_COLOR[c],
                                        values: dayKeys.map((k) => buckets.perDay.get(k)?.[c] ?? 0),
                                    }))}
                                />
                                <ChartLegend items={CHART_CATEGORIES.map((c) => ({ label: CATEGORY_LABEL[c], color: CATEGORY_COLOR[c] }))} />
                            </>
                        )}
                        {orgEvents.truncated && (
                            <p className="px-5 pb-4 text-[11.5px] text-[var(--faint)]">
                                A busy window — these charts cover the most recent {orgEvents.events.length.toLocaleString()} events.
                            </p>
                        )}
                    </Card>

                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card>
                            <CardHead title="Event speeds" note="share of recorded speeds" />
                            {orgEvents.isLoading ? (
                                <ChartLoading />
                            ) : buckets.speeds.length === 0 ? (
                                <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">No speeds recorded in this window.</p>
                            ) : (
                                <HBarList rows={speedBands} formatValue={(v) => `${v}%`} maxValue={100} />
                            )}
                        </Card>
                        <Card>
                            <CardHead title="Events by type" note={`Last ${DAYS} days`} />
                            {orgEvents.isLoading ? (
                                <ChartLoading />
                            ) : (
                                <HBarList
                                    rows={(Object.keys(buckets.perType) as EventCategory[])
                                        .filter((c) => buckets.perType[c] > 0 || c !== "other")
                                        .map((c) => ({ label: CATEGORY_LABEL[c], value: buckets.perType[c], color: CATEGORY_COLOR[c] }))}
                                />
                            )}
                        </Card>
                    </div>

                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card>
                            <CardHead title="Units active" note={`Last ${DAYS} days`} />
                            <GaugeArc
                                fraction={devices.length === 0 ? 0 : (devices.length - staleTwoWeeks.length) / devices.length}
                                big={devices.length === 0 ? "—" : `${Math.round(((devices.length - staleTwoWeeks.length) / devices.length) * 100)}%`}
                                caption={`${devices.length - staleTwoWeeks.length} of ${devices.length} units`}
                                color={staleTwoWeeks.length > 0 ? "var(--warn)" : "var(--safe)"}
                            />
                            {/* The roster of stale units deliberately isn't listed here — on a large
                                fleet that is a wall of names. The count is the signal; Needs
                                attention carries the individual trucks worth chasing. */}
                            <div className="flex flex-col items-center gap-2 px-5 pb-5 text-center text-[12.5px] text-[var(--muted)]">
                                {staleTwoWeeks.length > 0 ? (
                                    <Tag tone="crit">{staleTwoWeeks.length} not seen in 14 days</Tag>
                                ) : (
                                    <Tag tone="safe">Every unit reported this fortnight</Tag>
                                )}
                            </div>
                        </Card>
                        <Card>
                            <CardHead title="Pre-deployment tests" note="Last 24 hours" />
                            {preDeployment.isLoading ? (
                                <ChartLoading />
                            ) : (
                                <GaugeArc
                                    fraction={preDeployment.booted.length === 0 ? 0 : preDeployment.tested.length / preDeployment.booted.length}
                                    big={
                                        preDeployment.booted.length === 0
                                            ? "—"
                                            : `${Math.round((preDeployment.tested.length / preDeployment.booted.length) * 100)}%`
                                    }
                                    caption={`${preDeployment.tested.length} of ${preDeployment.booted.length} powered up`}
                                    color={
                                        preDeployment.booted.length > 0 && preDeployment.tested.length < preDeployment.booted.length
                                            ? "var(--warn)"
                                            : "var(--cyan)"
                                    }
                                />
                            )}
                            <div className="px-5 pb-5 text-center text-[12.5px] leading-relaxed text-[var(--muted)]">
                                {preDeployment.booted.length > 0
                                    ? "Trucks that tested lights and horn from the cab after powering on"
                                    : preDeployment.reporting === 0
                                      ? "No unit has reported a startup time, so this cannot be measured yet."
                                      : "No unit has been powered on in the last day."}
                            </div>
                        </Card>
                    </div>
                </div>

                <div className="flex min-w-0 flex-col gap-4">
                    <Card>
                        {/* Header and count are the way through to the same set as a full list. */}
                        <Link to={ATTENTION_LIST} className="block hover:bg-[var(--surface-2)]">
                            <CardHead title="Needs attention" note={attention.length > 0 ? "View all →" : undefined}>
                                {attention.length > 0 && (
                                    <Tag tone={attention.some((a) => a.tone === "crit") ? "crit" : "warn"}>{attention.length}</Tag>
                                )}
                            </CardHead>
                        </Link>
                        {fleetLoading ? (
                            <ChartLoading />
                        ) : attention.length === 0 ? (
                            <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">
                                Every unit has reported this fortnight and the fleet is on one firmware.
                            </p>
                        ) : (
                            <div className="mt-2 flex flex-col pb-2">
                                {/* `from` is what the unit's back link follows, so leaving a truck
                                    returns to this same filtered list rather than the whole fleet. */}
                                {attention.map((a) => (
                                    <Link
                                        key={a.device.id}
                                        to={`/devices/${a.device.id}`}
                                        state={{ from: ATTENTION_LIST, fromLabel: "Needs attention" }}
                                        className="flex items-center gap-3 border-t border-[var(--line)] px-5 py-3 text-left first:border-t-0 hover:bg-[var(--surface-2)]"
                                    >
                                        <span
                                            className="h-2.5 w-2.5 flex-none rounded-full"
                                            style={{ background: a.tone === "crit" ? "var(--crit)" : "var(--warn)" }}
                                        />
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-[13.5px] font-semibold">{a.title}</span>
                                            <span className="block truncate text-[12px] text-[var(--muted)]">{a.sub}</span>
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card>
                        <CardHead title="Latest events" />
                        {orgEvents.isLoading ? (
                            <ChartLoading />
                        ) : orgEvents.events.length === 0 ? (
                            <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">No events recorded yet.</p>
                        ) : (
                            <div className="mt-2 flex flex-col pb-2">
                                {orgEvents.events.slice(0, 8).map((e) => {
                                    const c = categoriseEvent(e);
                                    return (
                                        <Link
                                            key={e.id}
                                            to={`/events/${e.id}`}
                                            state={{ event: e }}
                                            className="flex items-center gap-3 border-t border-[var(--line)] px-5 py-3 first:border-t-0 hover:bg-[var(--surface-2)]"
                                        >
                                            <span className="h-2 w-2 flex-none rounded-full" style={{ background: CATEGORY_COLOR[c] }} />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-[13px] font-semibold">{CATEGORY_LABEL[c]}</span>
                                                <span className="block truncate text-[11.5px] text-[var(--muted)]">
                                                    {deviceSite(e.deviceId)} · {formatAgo(e.dateCreated)}
                                                </span>
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                        <div className="px-5 py-3">
                            <Link to="/events" className="text-[12.5px] font-semibold text-[var(--accent)]">
                                All events →
                            </Link>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

/**
 * One headline number. Pass `to` for the ones that answer "…and which units are those?" — the
 * whole tile becomes the link, so the obvious click lands where the reader was already headed.
 */
const KpiCard: React.FC<{
    title: string;
    icon: React.ReactNode;
    value: React.ReactNode;
    sub: string;
    subTone?: "safe" | "warn" | "crit";
    to?: string;
}> = ({ title, icon, value, sub, subTone, to }) => {
    const body = (
        <Card className={to ? "transition-colors hover:border-[var(--line-2)] hover:bg-[var(--surface-2)]" : ""}>
            <CardHead title={title} />
            <div className="flex items-center gap-3.5 px-5 pb-5 pt-3">
                <span className="grid h-12 w-12 flex-none place-items-center rounded-[13px] bg-[var(--surface-2)]">{icon}</span>
                <span className="min-w-0">
                    <span className="block whitespace-nowrap text-[28px] font-bold leading-tight tracking-tight tabular-nums">{value}</span>
                    <span
                        className={`mt-0.5 block truncate text-[12.5px] ${
                            subTone === "safe"
                                ? "text-[var(--safe)]"
                                : subTone === "warn"
                                  ? "text-[var(--warn)]"
                                  : subTone === "crit"
                                    ? "text-[var(--crit)]"
                                    : "text-[var(--muted)]"
                        }`}
                    >
                        {sub}
                    </span>
                </span>
            </div>
        </Card>
    );

    return to ? (
        <Link to={to} className="block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]">
            {body}
        </Link>
    ) : (
        body
    );
};

const ChartLoading: React.FC = () => (
    <div className="grid place-items-center p-10 text-[var(--muted)]">
        <Spinner />
    </div>
);
